/**
 * Text helpers for the menus: key names as the menus show them, and word wrap for the change list.
 */

const NAMES: Readonly<Record<string, string>> = {
  Space: 'SPACE',
  Escape: 'ESC',
  Enter: 'ENTER',
  ArrowLeft: 'ARROW LEFT',
  ArrowRight: 'ARROW RIGHT',
  ArrowUp: 'ARROW UP',
  ArrowDown: 'ARROW DOWN',
  ShiftLeft: 'LEFT SHIFT',
  ShiftRight: 'RIGHT SHIFT',
  ControlLeft: 'LEFT CTRL',
  ControlRight: 'RIGHT CTRL',
  AltLeft: 'LEFT ALT',
  AltRight: 'RIGHT ALT',
  Minus: '-',
  Equal: 'EQUALS',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  BracketLeft: 'LEFT BRACKET',
  BracketRight: 'RIGHT BRACKET',
  Backquote: 'BACKQUOTE',
  Backslash: 'BACKSLASH',
  NumpadAdd: 'NUMPAD +',
  NumpadSubtract: 'NUMPAD -',
  NumpadMultiply: 'NUMPAD TIMES',
  NumpadDivide: 'NUMPAD /',
  NumpadDecimal: 'NUMPAD .',
  NumpadEnter: 'NUMPAD ENTER',
};

/** Turn a KeyboardEvent.code into the upper-case name the menus show. */
export function keyName(code: string): string {
  if (code === '') {
    return '?';
  }
  const named = NAMES[code];
  if (named !== undefined) {
    return named;
  }
  const key = code.match(/^Key([A-Z])$/);
  if (key !== null) {
    return key[1] ?? '?';
  }
  const digit = code.match(/^Digit([0-9])$/);
  if (digit !== null) {
    return digit[1] ?? '?';
  }
  const numpad = code.match(/^Numpad([0-9])$/);
  if (numpad !== null) {
    return `NUMPAD ${numpad[1] ?? '?'}`;
  }
  if (/^F[0-9]{1,2}$/.test(code)) {
    return code;
  }
  const spaced = code.replace(/([a-z])([A-Z0-9])/g, '$1 $2').toUpperCase();
  const cleaned = spaced
    .replace(/[^A-Z0-9 ]/g, '')
    .replace(/ +/g, ' ')
    .trim();
  return cleaned === '' ? '?' : cleaned;
}

/** Greedy word wrap to at most width characters per line. */
export function wrapText(text: string, width: number): string[] {
  const words = text.split(/\s+/).filter((word) => word.length > 0);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (word.length > width) {
      if (current !== '') {
        lines.push(current);
        current = '';
      }
      // Cut into width-long pieces; the last piece stays open for the next word.
      const step = Math.max(1, width);
      let i = 0;
      for (; i + step < word.length; i += step) lines.push(word.slice(i, i + step));
      current = word.slice(i);
    } else if (current === '') {
      current = word;
    } else if (current.length + 1 + word.length <= width) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current !== '') {
    lines.push(current);
  }
  return lines;
}
